import { ThemeEvents } from '@theme/events';

/**
 * B&S Parfums design wiring (Zone A).
 * Replaces the design prototype script: every behavior it faked is wired here
 * to the host theme's real mechanisms (cart drawer, CartUpdateEvent,
 * VariantUpdateEvent) or implemented standalone (mobile menu, accordion,
 * gallery thumbs, sticky ATC reveal).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function on(selector, handler) {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest(selector);
    if (target) handler(target, event);
  });
}

/* --- Cart: open host drawer from the design's pill button --- */
on('[data-cart-toggle]', (_, event) => {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open();
  }
});

/* --- Cart: live count + total in the design header --- */
/* Bubbles that only make sense with something in the cart (mobile header icon). */
function toggleCartBadges(itemCount) {
  document.querySelectorAll('[data-cart-count-badge]').forEach((el) => {
    el.hidden = itemCount === 0;
  });
}

async function refreshCartSummary(itemCount) {
  if (typeof itemCount === 'number') {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(itemCount);
    });
    toggleCartBadges(itemCount);
  }
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
    });
    toggleCartBadges(cart.item_count);
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: cart.currency || 'EUR',
    }).format(cart.total_price / 100);
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = formatted.replace(/^(\D+)\s?/, '€');
    });
  } catch {
    /* header badge falls back to server-rendered values */
  }
}
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  refreshCartSummary(event.detail?.data?.itemCount);
});

/* --- Mobile menu (design's own markup) --- */
function closeMenu() {
  document.querySelector('.m-menu')?.classList.remove('open');
  document.querySelector('.m-menu__ov')?.classList.remove('open');
}
on('[data-menu-toggle]', () => {
  document.querySelector('.m-menu')?.classList.add('open');
  document.querySelector('.m-menu__ov')?.classList.add('open');
});
on('[data-menu-close]', closeMenu);
on('.m-menu__ov', closeMenu);

/* --- Mobile search: die Lupe auf der PDP klappt die Suchzeile im Header auf,
       statt auf /search zu navigieren. Ohne die Zeile bleibt der Link aktiv. --- */
on('[data-search-toggle]', (toggle, event) => {
  const row = document.querySelector('.m-search');
  if (!row) return;
  event.preventDefault();
  const open = row.classList.toggle('is-open');
  toggle.setAttribute('aria-expanded', String(open));
  if (open) {
    /** @type {HTMLInputElement | null} */
    const input = row.querySelector('input[type="search"]');
    input?.focus();
  }
});

/* --- Suchpopup offen halten, wenn der Fokus das Feld verlaesst ---
       Die Trefferliste hing allein an :focus-within. Auf dem iPhone nimmt der
       "Fertig"-Knopf der Tastatur dem Feld den Fokus, damit man die Treffer
       sehen kann — genau dann verschwanden sie. Jetzt traegt die Komponente
       eine .is-open-Klasse; die CSS zeigt das Popup auch ohne Fokus, solange
       etwas im Feld steht. Geschlossen wird per Klick daneben, Escape oder
       leerem Feld. --- */
const BS_SEARCH = '.hdrB__search--pred predictive-search-component';

function bsSearchComponent(node) {
  return node instanceof Element ? node.closest(BS_SEARCH) : null;
}

function bsCloseSearchPopups() {
  document.querySelectorAll(`${BS_SEARCH}.is-open`).forEach((el) => el.classList.remove('is-open'));
}

document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'search') return;
  const component = bsSearchComponent(input);
  if (!component) return;
  component.classList.toggle('is-open', input.value.trim().length > 0);
});

/* Fokus im Feld mit Text = auf; Fokus irgendwo sonst auf der Seite = zu.
   Ein reiner Blur (Tastatur zuklappen) loest kein focusin aus und laesst die
   Liste damit absichtlich stehen. */
document.addEventListener('focusin', (event) => {
  const component = bsSearchComponent(event.target);
  if (!component) {
    bsCloseSearchPopups();
    return;
  }
  /** @type {HTMLInputElement | null} */
  const input = component.querySelector('input[type="search"]');
  if (input && input.value.trim()) component.classList.add('is-open');
});

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('.hdrB__search--pred')) return;
  bsCloseSearchPopups();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') bsCloseSearchPopups();
});

/* --- Accordion (design markup, max-height transition) --- */
on('.acc__head', (head) => {
  const item = head.closest('.acc__item');
  const body = item?.querySelector('.acc__body');
  if (!item || !body) return;
  if (item.classList.contains('open')) {
    item.classList.remove('open');
    body.style.maxHeight = '0px';
  } else {
    item.classList.add('open');
    body.style.maxHeight = `${body.scrollHeight}px`;
  }
});
document.querySelectorAll('.acc__item.open .acc__body').forEach((body) => {
  body.style.maxHeight = `${body.scrollHeight}px`;
});

/* --- PDP gallery: thumb select + per-variant filtering --- */

/**
 * The real main image — never the peek layer the swipe inserts alongside it.
 * @param {Element} gallery
 * @returns {HTMLImageElement | null}
 */
function bsMainImage(gallery) {
  return /** @type {HTMLImageElement | null} */ (
    gallery.querySelector('.pdp__main .frozen-media:not(.pdp__peek) img')
  );
}

/* data-full is the 2000px master, which the zoom view genuinely needs. The main
   image does not: ask the CDN for what the box actually shows at this DPR. On a
   390px phone that is ~1200px, where 2000px would be ~2.8x the pixels for no
   visible gain. Never BELOW box x DPR though — that was the "low res" bug. */
/**
 * @param {HTMLElement} thumb
 * @param {Element} gallery
 * @returns {string | null}
 */
function bsMediaSrc(thumb, gallery) {
  const full = thumb.dataset.full;
  if (!full) return null;
  const box = gallery.querySelector('.pdp__main')?.clientWidth;
  if (!box) return full;
  const needed = box * (window.devicePixelRatio || 1);
  // Rounded to 100s so repeat views hit the same CDN variant instead of minting
  // a new render per device width.
  const width = Math.min(2000, Math.max(400, Math.ceil(needed / 100) * 100));
  return full.replace(/([?&]width=)\d+/, `$1${width}`);
}

/* The bottle style (multiply + hero shadow) hangs on the media LAYER, not on the
   gallery: the peek layer the swipe slides in alongside has to carry the style of
   ITS own image. Keyed off the gallery (`:has(.pdp__thumb--nobg.on)`) both layers
   necessarily shared one style, which is what flashed a photo with the bottle's
   multiply/shadow mid-swipe. */
/**
 * @param {HTMLElement | null} layer - a .frozen-media inside .pdp__main
 * @param {HTMLElement} thumb - the thumb whose image the layer shows
 */
function bsMarkLayerStyle(layer, thumb) {
  layer?.classList.toggle('is-nobg', thumb.classList.contains('pdp__thumb--nobg'));
}

/**
 * Hide the arrows while there is nowhere to step — a variant filter can leave a
 * single visible thumb, and a one-image gallery renders none at all.
 * @param {Element} gallery
 */
function bsSyncGalleryNav(gallery) {
  const stuck = bsVisibleThumbs(gallery).length < 2;
  gallery.querySelectorAll('.pdp__nav').forEach((nav) => {
    /** @type {HTMLElement} */ (nav).hidden = stuck;
  });
}

/* Picture and style have to change TOGETHER. An <img> keeps painting its PREVIOUS
   picture until the new src has decoded, so flipping the class the moment src is
   assigned dresses the old image in the new style for as long as the download
   takes. On a cold cache that is not a flash but a lasting wrong picture: the
   photo with its normal background, multiplied against the stage and carrying the
   flakon's hero shadow — drop-shadow traces the opaque rectangle of a photo,
   which is why it reads as a box around it.

   So a layer never gets a new `src`. It gets a new <img>, decoded off-DOM before
   it is ever inserted: an element that is already complete paints its picture in
   the very frame it appears, which is the same frame the class flips. There is no
   window left in which the two can disagree — the swipe's peek layer works the
   same way, and landing a swipe simply promotes it.

   Deliberately without a deadline. Giving up after N ms means choosing between two
   lies — the old picture in the new style, or an empty stage — while the honest
   state is right there: the previous image in its OWN style, with the thumb strip
   already showing what was picked. A slow file therefore delays the swap and
   nothing else, and bsPrefetchNeighbours makes sure it is rarely slow at all. */

/**
 * An <img> for `src` that is NOT in the document yet, carrying the class of the
 * one it will replace and the alt text of the thumb it comes from.
 * @param {string} src
 * @param {HTMLElement} thumb - the thumb whose image this is
 * @param {HTMLImageElement} current - the image it is built to replace
 * @returns {HTMLImageElement}
 */
function bsBuildImage(src, thumb, current) {
  const img = document.createElement('img');
  img.className = current.className || 'frozen-img';
  img.decoding = 'async';
  img.alt = /** @type {HTMLImageElement | null} */ (thumb.querySelector('img'))?.alt ?? '';
  img.src = src;
  return img;
}

/**
 * Resolve once `img` is decoded and can be painted without delay. Never rejects:
 * a file that cannot be loaded still gets inserted, so the stage goes empty
 * rather than keeping a picture the selected thumb does not stand for.
 * @param {HTMLImageElement} img
 * @returns {Promise<void>}
 */
function bsWarmImage(img) {
  try {
    return img.decode().catch(() => {});
  } catch {
    // decode() is baseline everywhere we support, but a missing one would throw
    // synchronously and strand every swap — too central to leave unguarded.
    return new Promise((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
}

/* Guards the async swap: taps land faster than images decode, and a variant change
   can cut in mid-load. Only the most recent pick may still touch the stage. */
let bsMediaPick = 0;

/**
 * Strip and sticky bar follow the CHOICE, not the pixels: neither can look wrong
 * while an image is still on its way, so both move the moment a thumb is picked.
 * @param {Element} gallery
 * @param {HTMLElement} thumb
 */
function bsMarkThumb(gallery, thumb) {
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => t.classList.remove('on'));
  thumb.classList.add('on');
  bsSyncStickyImg(gallery);
}

/**
 * @param {Element} gallery
 * @param {HTMLElement} thumb
 * @returns {Promise<void>} settles once the stage shows this thumb's image
 */
async function bsSelectMedia(gallery, thumb) {
  const mainImg = bsMainImage(gallery);
  if (!mainImg || !thumb) return;
  const pick = ++bsMediaPick;
  bsMarkThumb(gallery, thumb);
  const layer = /** @type {HTMLElement | null} */ (mainImg.closest('.frozen-media'));
  // Never copy the 400px thumbnail src into the main — that was the "low res" bug.
  const src = bsMediaSrc(thumb, gallery);
  if (!src || mainImg.getAttribute('src') === src) {
    // Nothing to load: the picture on the stage is already the right one.
    bsMarkLayerStyle(layer, thumb);
    return;
  }
  const fresh = bsBuildImage(src, thumb, mainImg);
  await bsWarmImage(fresh);
  // A newer pick (a second tap, an arrow held down, a variant change) owns the
  // stage now — this one lost the race and must keep its hands off.
  if (pick !== bsMediaPick || !mainImg.isConnected) return;
  mainImg.replaceWith(fresh);
  bsMarkLayerStyle(layer, thumb);
  bsPrefetchNeighbours(gallery);
}

/* One step ahead: the swipe's peek layer has to show the neighbour the instant a
   finger moves, and a tap on the next thumb should not wait for a download — an
   image that is already decoded swaps in the same frame, which is what keeps the
   stage from ever wearing a foreign style. Idle time only, once per URL and never
   on a metered or slow connection: the main image is the LCP element of the PDP
   and nothing here may compete with it. */
const bsWarmedSrcs = new Set();

/** @param {Element} gallery */
function bsPrefetchNeighbours(gallery) {
  const conn = /** @type {any} */ (navigator).connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  const run = () => {
    [-1, 1].forEach((direction) => {
      const target = bsNeighbourThumb(gallery, direction);
      const src = target && bsMediaSrc(target, gallery);
      if (!src || bsWarmedSrcs.has(src)) return;
      bsWarmedSrcs.add(src);
      const probe = new Image();
      probe.decoding = 'async';
      probe.src = src;
    });
  };
  const idle = /** @type {any} */ (window).requestIdleCallback;
  if (typeof idle === 'function') idle(run, { timeout: 2000 });
  else setTimeout(run, 500);
}

/**
 * The sticky bar shows WHAT goes into the cart, so its thumbnail follows the
 * product/variant — not what the visitor is browsing in the gallery. It mirrors
 * the FIRST visible thumb (the nobg flakon, or the variant's own photo, which
 * bsFilterGallery prepends), never the clicked one: opening the Zutaten shot
 * used to drag that ingredient graphic into the bar.
 * @param {Element} gallery
 */
function bsSyncStickyImg(gallery) {
  const stickyImg = document.querySelector('[data-bs-sticky-img] img');
  if (!stickyImg) return;
  // Copy the THUMB's source, not data-full — a 2000px file behind a 42px box
  // would be a pointless download.
  const thumbImg = bsVisibleThumbs(gallery)[0]?.querySelector('img');
  if (!thumbImg) return;
  const src = thumbImg.currentSrc || thumbImg.src;
  if (!src || stickyImg.src === src) return;
  stickyImg.removeAttribute('srcset');
  stickyImg.removeAttribute('sizes');
  stickyImg.src = src;
}
on('.pdp__thumb', (thumb) => {
  const gallery = thumb.closest('.pdp__gallery');
  if (gallery) bsSelectMedia(gallery, thumb);
});

/* Arrows from the mobile mockup: a third way into the same thumb strip that tap
   and swipe already drive, so the variant filter and the sticky-bar mirror keep
   working untouched. Wraps at both ends, exactly like the swipe does. */
on('.pdp__nav', (nav) => {
  const gallery = nav.closest('.pdp__gallery');
  const step = Number(/** @type {HTMLElement} */ (nav).dataset.galleryStep);
  if (gallery && step) bsStepMedia(gallery, step);
});

/* First paint: Liquid already hides the thumbs of other variants, so a gallery
   can start with a single visible image even though it rendered arrows. */
document.querySelectorAll('.pdp__gallery').forEach((gallery) => {
  bsSyncGalleryNav(gallery);
  // Warming the neighbours waits for `load`: the main image is the LCP element,
  // and a second gallery image must never be fetched alongside it.
  if (document.readyState === 'complete') bsPrefetchNeighbours(gallery);
  else window.addEventListener('load', () => bsPrefetchNeighbours(gallery), { once: true });
});

/* Swipe left/right on the main image to step through the gallery (touch only).
   Steps through the same thumb strip the taps use, so the variant filter (hidden
   thumbs) and the sticky-bar mirror keep working — this is only a second way in.

   The image tracks the finger: the neighbour is rendered into a peek layer that
   slides in alongside, so a half-swipe shows what is coming and springs back if
   released short. Without that the image just snaps at the end and the gesture
   reads as a button, not a carousel. */
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_AXIS_LOCK = 8;
const SWIPE_SLIDE_MS = 220;

/**
 * @param {Element} gallery
 * @returns {HTMLElement[]}
 */
function bsVisibleThumbs(gallery) {
  const thumbs = /** @type {HTMLElement[]} */ (Array.from(gallery.querySelectorAll('.pdp__thumb')));
  return thumbs.filter((t) => !t.hidden);
}

/**
 * The thumb `direction` steps away from the active one, wrapping at both ends.
 * @param {Element} gallery
 * @param {number} direction
 * @returns {HTMLElement | null}
 */
function bsNeighbourThumb(gallery, direction) {
  const thumbs = bsVisibleThumbs(gallery);
  if (thumbs.length < 2) return null;
  // The active thumb can be filtered out by a variant switch; start from the
  // first visible one then, so a backwards swipe doesn't land near the end.
  const current = thumbs.findIndex((t) => t.classList.contains('on'));
  const next = current === -1 ? 0 : (current + direction + thumbs.length) % thumbs.length;
  return thumbs[next] ?? null;
}

/**
 * @param {Element} gallery
 * @param {number} direction
 */
function bsStepMedia(gallery, direction) {
  const target = bsNeighbourThumb(gallery, direction);
  if (target) bsSelectMedia(gallery, target);
}

/**
 * Lazily built second media layer, appended AFTER the real one so that
 * bsMainImage() and any `.pdp__main img` lookup still find the original.
 * @param {HTMLElement} main
 * @returns {HTMLElement}
 */
function bsPeekLayer(main) {
  const existing = /** @type {HTMLElement | null} */ (main.querySelector('.pdp__peek'));
  if (existing) return existing;
  const peek = document.createElement('div');
  peek.className = 'frozen-media pdp__peek';
  peek.setAttribute('aria-hidden', 'true');
  peek.hidden = true;
  const img = document.createElement('img');
  img.className = 'frozen-img';
  img.alt = '';
  peek.appendChild(img);
  main.appendChild(peek);
  return peek;
}

/**
 * @typedef {object} BsSwipeState
 * @property {number} id - identifier of the finger that owns the gesture
 * @property {number} x
 * @property {number} y
 * @property {number} lastX - last tracked position, so a cancel can still settle
 * @property {HTMLElement} main - .pdp__main
 * @property {Element} gallery - .pdp__gallery
 * @property {HTMLElement} media - the real .frozen-media layer
 * @property {'x' | 'y' | null} axis - locked on the first decisive movement
 * @property {number} direction - -1 previous, 1 next, 0 undecided
 * @property {HTMLElement | null} peek
 * @property {HTMLElement | null} target - thumb the gesture would land on
 * @property {number} width
 */

/** @type {BsSwipeState | null} */
let bsSwipe = null;

/* A finished drag is followed by a synthetic click. Swallow it, so the zoom view
   we hang on the main image later opens on taps only, never on swipes. */
let bsSwipeEndedAt = -Infinity;
const SWIPE_CLICK_GUARD_MS = 500;

/**
 * Point the peek layer at the neighbour in `direction` and park it off-screen.
 * @param {BsSwipeState} state
 * @param {number} direction
 */
function bsPreparePeek(state, direction) {
  const target = bsNeighbourThumb(state.gallery, direction);
  if (!target) return false;
  const peek = bsPeekLayer(state.main);
  const img = /** @type {HTMLImageElement | null} */ (peek.querySelector('img'));
  const src = bsMediaSrc(target, state.gallery);
  // Same image the landing will promote into the main layer. The element is
  // REPLACED rather than re-pointed: a reused <img> keeps painting its old picture
  // until the new one decodes, so re-aiming the peek mid-drag would slide the
  // previous neighbour in wearing the new style. A fresh element has nothing to
  // show until its own file is there, and bsPrefetchNeighbours has usually warmed
  // that file already — so there is normally no gap to bridge.
  if (img && src && img.getAttribute('src') !== src) img.replaceWith(bsBuildImage(src, target, img));
  // The incoming image brings its own style — set before the layer is shown.
  bsMarkLayerStyle(peek, target);
  peek.hidden = false;
  state.peek = peek;
  state.target = target;
  state.direction = direction;
  return true;
}

/**
 * @param {BsSwipeState} state
 * @param {number} deltaX
 */
function bsDrawSwipe(state, deltaX) {
  const { media, peek, direction, width } = state;
  media.style.transform = `translate3d(${deltaX}px, 0, 0)`;
  // Incoming image sits one full box away on the side it travels in from.
  if (peek) peek.style.transform = `translate3d(${deltaX + direction * width}px, 0, 0)`;
}

/** @param {BsSwipeState} state */
function bsResetSwipeLayers(state) {
  const { media, peek } = state;
  media.style.transition = '';
  media.style.transform = '';
  if (peek) {
    peek.style.transition = '';
    peek.style.transform = '';
    peek.hidden = true;
  }
  delete state.main.dataset.sliding;
}

/**
 * Settle the drag: `commit` slides the neighbour in, otherwise it springs back.
 * @param {BsSwipeState} state
 * @param {boolean} commit
 */
function bsSettleSwipe(state, commit) {
  const { media, peek, direction, width, gallery, target } = state;
  const transition = `transform ${SWIPE_SLIDE_MS}ms ease-out`;
  state.main.dataset.sliding = '1';
  media.style.transition = transition;
  media.style.transform = commit ? `translate3d(${-direction * width}px, 0, 0)` : 'translate3d(0, 0, 0)';
  if (peek) {
    peek.style.transition = transition;
    peek.style.transform = commit ? 'translate3d(0, 0, 0)' : `translate3d(${direction * width}px, 0, 0)`;
  }

  // Fixed timer rather than transitionend: the latter never fires when the drag
  // happens to end exactly on the target offset.
  setTimeout(() => {
    if (!commit || !target) {
      bsResetSwipeLayers(state);
      return;
    }
    bsCommitPeek(state, target);
  }, SWIPE_SLIDE_MS + 20);
}

/**
 * Land a committed swipe. The peek layer is ALREADY showing the target image, in
 * its own style, exactly over the box — so promote it instead of copying its src
 * onto the main layer. Copying meant the cover dropped while the main image was
 * still loading its new source, and an <img> keeps painting its previous picture
 * until then: that is the photo with the normal background flashing back in, and
 * the Zutaten shot wearing the flakon's hero shadow, right AFTER the slide had
 * come to rest. Handing over the <img> element itself has nothing to load and
 * nothing to race — the two layers just trade images, in one task, under cover.
 * @param {BsSwipeState} state
 * @param {HTMLElement} target
 */
function bsCommitPeek(state, target) {
  const { gallery, media, peek } = state;
  const mainImg = media.querySelector('img');
  const peekImg = peek?.querySelector('img');
  if (!peek || !mainImg || !peekImg) {
    // No peek to promote (reduced motion, or a neighbour that never got one).
    // bsSelectMedia decodes before it swaps, so this path cannot lie either — but
    // it waits as long as the file needs, and the cover must not: dropping it
    // early shows the previous picture in its OWN style, which is honest, while
    // holding it would leave `sliding` set and block the next gesture.
    Promise.race([
      bsSelectMedia(gallery, target),
      new Promise((resolve) => setTimeout(resolve, SWIPE_SLIDE_MS * 2)),
    ]).then(() => bsResetSwipeLayers(state));
    return;
  }
  // Straight swap, so the peek keeps an <img> for the next gesture: the one it
  // hands over is decoded and on screen, the one it takes back is hidden anyway.
  const slot = document.createComment('');
  mainImg.replaceWith(slot);
  peekImg.replaceWith(mainImg);
  slot.replaceWith(peekImg);
  bsMarkLayerStyle(media, target);
  // Nothing of ours is in flight now, and a bsSelectMedia still waiting on a
  // decode must not paint over the image the finger just brought in.
  bsMediaPick++;
  bsMarkThumb(gallery, target);
  bsResetSwipeLayers(state);
  bsPrefetchNeighbours(gallery);
}

/**
 * @param {TouchList} list
 * @param {number} id
 * @returns {Touch | null}
 */
function bsFindTouch(list, id) {
  return Array.from(list).find((touch) => touch.identifier === id) ?? null;
}

/* Touch events, not pointer events, and deliberately so: the move listener has
   to be non-passive. Pointer events here were passive by necessity, which left
   preventDefault() unavailable and the gesture at the mercy of `touch-action`
   alone — iOS Safari claims a horizontal drag anyway and fires pointercancel
   mid-swipe, which read as the image springing back under the finger.

   Scoped to the element rather than the document so the non-passive listener
   costs the rest of the page nothing. Touch events never fire for a mouse, so
   desktop is untouched. */
const pdpMain = /** @type {HTMLElement | null} */ (document.querySelector('.pdp__main'));

if (pdpMain) {
  pdpMain.addEventListener(
    'touchstart',
    (event) => {
      // A second finger means pinch, not swipe — hand the image back and get out
      // of the way (spring back rather than snap, the pinch is still running).
      if (bsSwipe) {
        const running = bsSwipe;
        bsSwipe = null;
        if (running.target) bsSettleSwipe(running, false);
        return;
      }
      if (event.touches.length > 1) return;
      const touch = event.touches[0];
      const gallery = pdpMain.closest('.pdp__gallery');
      const media = /** @type {HTMLElement | null} */ (
        pdpMain.querySelector('.frozen-media:not(.pdp__peek)')
      );
      if (!touch || !gallery || !media) return;
      // Mid-animation: let it land rather than fight it.
      if (pdpMain.dataset.sliding) return;
      // Single-image products (or a variant filter that left one thumb) have
      // nothing to swipe to — don't arm the gesture at all.
      if (bsVisibleThumbs(gallery).length < 2) return;
      bsSwipe = {
        id: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        lastX: touch.clientX,
        main: pdpMain,
        gallery,
        media,
        axis: null,
        direction: 0,
        peek: null,
        target: null,
        width: pdpMain.clientWidth || 1,
      };
    },
    { passive: true }
  );

  pdpMain.addEventListener(
    'touchmove',
    (event) => {
      if (!bsSwipe) return;
      const touch = bsFindTouch(event.touches, bsSwipe.id);
      if (!touch) return;
      const deltaX = touch.clientX - bsSwipe.x;
      const deltaY = touch.clientY - bsSwipe.y;

      if (!bsSwipe.axis) {
        if (Math.abs(deltaX) < SWIPE_AXIS_LOCK && Math.abs(deltaY) < SWIPE_AXIS_LOCK) return;
        // Lock the axis on the first decisive movement. Vertical belongs to the
        // page: drop the gesture and let the browser scroll as usual.
        bsSwipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (bsSwipe.axis === 'y') {
          bsSwipe = null;
          return;
        }
      }
      if (bsSwipe.axis !== 'x') return;

      // Horizontal is ours from here on. THIS is what keeps the browser from
      // turning the drag into a scroll and cancelling it half-way.
      if (event.cancelable) event.preventDefault();
      bsSwipe.lastX = touch.clientX;
      if (REDUCED_MOTION) return;

      // Direction can flip mid-drag (drag left, change your mind, drag back past
      // the start) — re-aim the peek at the other neighbour when it does.
      const direction = deltaX < 0 ? 1 : -1;
      if (direction !== bsSwipe.direction) bsPreparePeek(bsSwipe, direction);
      if (bsSwipe.target) bsDrawSwipe(bsSwipe, deltaX);
    },
    { passive: false }
  );

  pdpMain.addEventListener(
    'touchend',
    (event) => {
      if (!bsSwipe) return;
      const state = bsSwipe;
      const touch = bsFindTouch(event.changedTouches, state.id);
      if (!touch && event.touches.length) return;
      bsSwipe = null;
      if (state.axis !== 'x') return;

      bsSwipeEndedAt = performance.now();
      const deltaX = (touch ? touch.clientX : state.lastX) - state.x;
      const passed = Math.abs(deltaX) >= SWIPE_MIN_DISTANCE;
      if (REDUCED_MOTION) {
        // No tracking layer to settle: step straight to the neighbour.
        if (passed) bsStepMedia(state.gallery, deltaX < 0 ? 1 : -1);
        return;
      }
      if (!state.target) return;
      bsSettleSwipe(state, passed);
    },
    { passive: true }
  );

  pdpMain.addEventListener(
    'touchcancel',
    () => {
      if (!bsSwipe) return;
      const state = bsSwipe;
      bsSwipe = null;
      if (!state.target) return;
      // A cancel this late is the system interrupting (call, gesture recogniser).
      // The finger had already said where it wanted to go — honour it rather than
      // throwing the drag away.
      const passed = Math.abs(state.lastX - state.x) >= SWIPE_MIN_DISTANCE;
      bsSwipeEndedAt = performance.now();
      bsSettleSwipe(state, passed);
    },
    { passive: true }
  );
}

document.addEventListener(
  'click',
  (event) => {
    if (performance.now() - bsSwipeEndedAt > SWIPE_CLICK_GUARD_MS) return;
    if (!(event.target instanceof Element) || !event.target.closest('.pdp__main')) return;
    // Capture phase, so this lands before any handler on the image itself.
    event.stopPropagation();
    event.preventDefault();
  },
  true
);

/* Show only the selected variant's media (+ media assigned to no variant);
   swap the main image to the variant's featured media. */
function bsFilterGallery(variant) {
  const gallery = document.querySelector('.pdp__gallery');
  if (!gallery || !variant) return;
  const curId = String(variant.id);
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => {
    const ids = (t.dataset.variantIds || '').trim();
    t.hidden = ids !== '' && !ids.split(/\s+/).includes(curId);
  });
  const fmId = variant.featured_media && String(variant.featured_media.id);
  let target = fmId && gallery.querySelector(`.pdp__thumb[data-media-id="${fmId}"]`);
  if (!target) target = gallery.querySelector('.pdp__thumb:not([hidden])');
  // Move the variant's own media to the front, mirroring the Liquid ordering on
  // first paint. Without this the thumb keeps its initial DOM slot, so switching
  // the variant leaves the variant-less vibe shot sitting in position 1.
  const strip = target && target.parentElement;
  if (strip && strip.firstElementChild !== target) strip.prepend(target);
  if (target) bsSelectMedia(gallery, target);
  bsSyncGalleryNav(gallery);
}

/* --- Sticky ATC: reveal when the buy box leaves the viewport --- */
const buyBox = document.querySelector('.pdp__buy');
const stickyBar = document.querySelector('.pdp-sticky');
if (buyBox && stickyBar) {
  const update = () => {
    const rect = buyBox.getBoundingClientRect();
    stickyBar.classList.toggle('show', rect.bottom < 80);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
  // Sticky ATC submits the main product form
  stickyBar.querySelector('[data-bs-sticky-atc]')?.addEventListener('click', (event) => {
    event.preventDefault();
    if (event.currentTarget.disabled) return;
    const form = document.querySelector('.pdp__buybox product-form-component form');
    if (form) form.requestSubmit();
  });
}

/* --- PDP performance bars: values come from the product's custom.rating_*
       metafields, rendered into data-value by main-product.liquid. Ratings are
       on a 0-10 scale; the bars are a percentage of 10. --- */
const ratings = document.querySelector('[data-bs-ratings]');
if (ratings) {
  const fmt = (v) => v.toFixed(1).replace('.', ',');
  /** @type {Array<[HTMLElement, number]>} */
  const bars = [];

  for (const metric of ratings.querySelectorAll('[data-metric]')) {
    const value = parseFloat(metric.dataset.value ?? '');
    if (Number.isNaN(value)) {
      metric.remove();
      continue;
    }
    const bar = /** @type {HTMLElement|null} */ (metric.querySelector('[data-bar-fill]'));
    const display = metric.querySelector('[data-value-display]');
    if (display) display.textContent = fmt(value);
    if (bar) {
      if (!REDUCED_MOTION) bar.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      bars.push([bar, value]);
    }
  }

  if (!bars.length) {
    ratings.remove();
  } else {
    requestAnimationFrame(() => {
      ratings.hidden = false;
      requestAnimationFrame(() => {
        for (const [bar, value] of bars) bar.style.width = `${(value / 10) * 100}%`;
      });
    });
  }
}

/* --- Variant change: filter the gallery + mirror the live price into the sticky bar --- */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = event.detail?.resource;
  bsFilterGallery(variant);
  // Mirror availability into the sticky ATC. The inline buy button is handled
  // server-side + by product-form.js, but the floating sticky mirror has no
  // such wiring — without this it stays clickable on a sold-out variant.
  const stickyAtc = document.querySelector('.pdp-sticky [data-bs-sticky-atc]');
  if (stickyAtc && variant) {
    const available = variant.available !== false;
    stickyAtc.disabled = !available;
    stickyAtc.classList.toggle('is-disabled', !available);
    stickyAtc.textContent = available ? 'In den Warenkorb' : 'Ausverkauft';
  }
  if (variant) {
    const stockLine = document.querySelector('[data-bs-stock]');
    const stockText = stockLine?.querySelector('[data-bs-stock-text]');
    if (stockLine && stockText) {
      const available = variant.available !== false;
      stockLine.classList.toggle('is-out', !available);
      stockText.textContent = available ? 'Auf Lager - in 1-3 Werktagen bei dir!' : 'Nicht vorrätig';
    }
    // The size option's position is baked in by Liquid — it is not always option1.
    const stickyVariant = document.querySelector('[data-bs-sticky-variant]');
    if (stickyVariant) {
      const position = stickyVariant.dataset.bsSizePosition || '1';
      stickyVariant.textContent = variant[`option${position}`] || '';
    }
  }
  // product-price replaces the main price container after this event; copy on next tick.
  setTimeout(() => {
    const main = document.querySelector('.pdp__price');
    const sticky = document.querySelector('.pdp-sticky__price');
    if (main && sticky) sticky.textContent = main.textContent.trim();
  }, 0);
});

/* --- Reduced-motion: disable max-height animation --- */
if (REDUCED_MOTION) {
  document.querySelectorAll('.acc__body').forEach((body) => {
    body.style.transition = 'none';
  });
}

/* --- Announcement bar: rotate messages every 5s + working prev/next arrows
       (replaces the old theme's multi-message bar; texts are section settings). --- */
document.querySelectorAll('[data-announce-rotator]').forEach((rotator) => {
  const msgs = [...rotator.querySelectorAll('.hdrB__announce-msg')];
  if (msgs.length < 2) return;
  let idx = msgs.findIndex((m) => m.classList.contains('is-active'));
  if (idx < 0) idx = 0;
  let timer;
  const show = (n) => {
    msgs[idx].classList.remove('is-active');
    idx = (n + msgs.length) % msgs.length;
    msgs[idx].classList.add('is-active');
  };
  const start = () => {
    timer = setInterval(() => show(idx + 1), 5000);
  };
  const reset = () => {
    clearInterval(timer);
    start();
  };
  rotator.querySelector('[data-announce-prev]')?.addEventListener('click', () => {
    show(idx - 1);
    reset();
  });
  rotator.querySelector('[data-announce-next]')?.addEventListener('click', () => {
    show(idx + 1);
    reset();
  });
  start();
});

/* --- Header: semi-sticky, Verhalten 1:1 wie im Dawn-Vorgaengershop
       (dort sections/header.liquid, class StickyHeader).

       Oben in der Seite steht der Kopf im Fluss und scrollt komplett weg.
       Unterhalb der Kopfhoehe klebt er: runterscrollen versteckt ihn,
       hochscrollen holt ihn zurueck. Geklebt zeigt er nur die Kopfzeile —
       Announcement- und TrustedShops-Band parken per negativem `top` ueber der
       Fensterkante und tauchen erst wieder auf, wenn man ganz oben ist.

       Der Umschaltpunkt zurueck in den Fluss ist die Bandhoehe (nicht 0), weil
       der Kopf dort exakt gleich steht wie geklebt — so springt nichts. Das ist
       dieselbe Schwelle wie PPs `headerBounds.top` (dort beginnt die
       Header-Section unter der Announcement-Section). --- */
const bsHeaderGroup = /** @type {HTMLElement | null} */ (document.querySelector('#header-group'));
if (bsHeaderGroup) {
  const STUCK = 'bs-head-stuck';
  const HIDDEN = 'bs-head-hidden';
  const ANIMATE = 'bs-head-animate';

  /** Hoehe der Baender ueber der Kopfzeile (Announcement + TrustedShops). */
  let stripHeight = 0;
  /** Gesamthoehe des Kopfes. */
  let fullHeight = 0;

  /** Desktop- und Mobil-Kopf sind getrennte Baeume, per display umgeschaltet. */
  const visibleHead = () => {
    for (const head of bsHeaderGroup.querySelectorAll('.d-head, .m-head')) {
      if (/** @type {HTMLElement} */ (head).offsetParent !== null) return head;
    }
    return null;
  };

  const measure = () => {
    fullHeight = bsHeaderGroup.offsetHeight;
    const head = visibleHead();
    const strip = head?.querySelector('.trustedshops-bar') ?? head?.querySelector('.hdrB__util, .m-announce');
    // Differenz statt offsetHeight-Summe: so zaehlen die Safe-Area-Leiste
    // (.m-head::before) und jeder Rand zwischen den Baendern mit.
    stripHeight =
      head && strip ? Math.max(0, Math.round(strip.getBoundingClientRect().bottom - head.getBoundingClientRect().top)) : 0;
    bsHeaderGroup.style.setProperty('--bs-head-strip', `${stripHeight}px`);
    bsHeaderGroup.style.setProperty('--bs-head-full', `${fullHeight}px`);
  };

  const hide = () => {
    bsHeaderGroup.classList.add(STUCK, HIDDEN);
  };

  const reveal = () => {
    bsHeaderGroup.classList.add(STUCK, ANIMATE);
    bsHeaderGroup.classList.remove(HIDDEN);
  };

  const reset = () => {
    bsHeaderGroup.classList.remove(STUCK, HIDDEN, ANIMATE);
  };

  // Desktop: der Kopf klebt dauerhaft, ohne Ein-/Ausfahren. Nur die Baender
  // parken ueber der Fensterkante (negativer `top` aus STUCK) — sie kommen
  // erst bei scrollTop 0 zurueck.
  const stick = () => {
    bsHeaderGroup.classList.add(STUCK);
    bsHeaderGroup.classList.remove(HIDDEN, ANIMATE);
  };

  // Solange die Trefferliste offen ist, bleibt der Kopf stehen — sonst reisst
  // ihn der Scroll unter dem Popup weg (PP macht dasselbe fuer predictive-search).
  const holdsHeader = () =>
    document.querySelector('.hdrB__search--pred predictive-search-component.is-open') != null ||
    document.querySelector('.m-menu.open') != null;

  // Breakpoint wie design.css (.d-head / .m-head schalten bei 700px um).
  const desktop = window.matchMedia('(min-width: 701px)');

  let lastScroll = window.pageYOffset;
  let ticking = false;

  const update = () => {
    ticking = false;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop <= stripHeight) {
      reset();
    } else if (desktop.matches) {
      stick();
    } else if (scrollTop > fullHeight && !holdsHeader()) {
      if (scrollTop > lastScroll) hide();
      else if (scrollTop < lastScroll) reveal();
    }

    lastScroll = scrollTop;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  measure();
  update();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      update();
    },
    { passive: true }
  );

  // Das TrustedShops-Widget laedt asynchron und der Announcement-Rotator
  // wechselt Texte — beides kann die Bandhoehe aendern.
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(measure);
    observer.observe(bsHeaderGroup);
  }
}
